/**
 * Interactive block: actions, calculator, selector
 * Block with user interaction capabilities.
 *
 * Vertical-aware: renders structural DOM differences for
 * restaurant (bistro chalkboard), ecommerce (curated boutique), saas (developer dashboard),
 * and agency (campaign brief badge).
 */

import { registerBlockRenderer, getBlockRendererContext } from './registry';
import { escapeHtml, createBlockWrapper, evaluateFormula, disableButtonTemporarily } from './utils';
import { navigateToUrl } from '../../core/navigation';
import type { InteractiveData } from './types';

// =============================================================================
// INTERACTIVE BLOCK
// =============================================================================

registerBlockRenderer('interactive', (data: unknown, container: HTMLElement, onAction?: (value: string) => void) => {
  const interactiveData = data as InteractiveData;
  const vertical = getBlockRendererContext().vertical;

  const wrapper = createBlockWrapper('interactive');

  // SaaS: traffic-light dots (red/yellow/green) like a terminal window
  if (vertical === 'saas') {
    const dots = document.createElement('div');
    dots.className = 'pms-terminal-dots';
    wrapper.prepend(dots);
  }

  // Restaurant: kitchen ticket stamp (ORDER #)
  if (vertical === 'restaurant') {
    const stamp = document.createElement('div');
    stamp.className = 'pms-ticket-stamp';
    stamp.textContent = 'ORDER #' + Math.floor(Math.random() * 900 + 100);
    wrapper.prepend(stamp);
  }

  // Ecommerce: bag handle icon
  if (vertical === 'ecommerce') {
    const bag = document.createElement('div');
    bag.className = 'pms-bag-icon';
    wrapper.prepend(bag);
  }

  // Healthcare: medical cross icon
  if (vertical === 'healthcare') {
    const cross = document.createElement('div');
    cross.className = 'pms-medical-cross';
    wrapper.prepend(cross);
  }

  // Finance: vault indicator
  if (vertical === 'finance') {
    const indicator = document.createElement('div');
    indicator.className = 'pms-vault-indicator';
    indicator.textContent = 'SECURED';
    wrapper.prepend(indicator);
  }

  // Real estate: blueprint compass marker
  if (vertical === 'real_estate') {
    const marker = document.createElement('div');
    marker.className = 'pms-blueprint-marker';
    wrapper.prepend(marker);
  }

  // Portfolio: CMY color dots (creative studio swatches)
  if (vertical === 'portfolio') {
    const swatches = document.createElement('div');
    swatches.className = 'pms-studio-dots';
    wrapper.prepend(swatches);
  }

  // Automotive: RPM gauge badge + redline indicator
  if (vertical === 'automotive') {
    const badge = document.createElement('div');
    badge.className = 'pms-gauge-badge';
    badge.textContent = 'RPM';
    wrapper.prepend(badge);
    const redline = document.createElement('div');
    redline.className = 'pms-redline-dot';
    wrapper.prepend(redline);
  }

  // Dental: appointment badge + status dot + time slot (like automotive RPM + redline)
  if (vertical === 'dental') {
    const badge = document.createElement('div');
    badge.className = 'pms-dental-badge';
    // Dynamic time slot (like restaurant's random order #)
    const hours = [9, 10, 11, 14, 15, 16];
    const mins = ['00', '15', '30', '45'];
    const dh = hours[Math.floor(Math.random() * hours.length)];
    const dm = mins[Math.floor(Math.random() * mins.length)];
    badge.innerHTML =
      `<span class="pms-dental-badge-label">APPT</span>` +
      `<span class="pms-dental-badge-time">${dh}:${dm}</span>`;
    wrapper.prepend(badge);

    // Availability dot (like automotive redline dot)
    const statusDot = document.createElement('div');
    statusDot.className = 'pms-dental-status-dot';
    wrapper.prepend(statusDot);
  }

  // Agency: campaign brief badge
  if (vertical === 'agency') {
    const badge = document.createElement('div');
    badge.className = 'pms-campaign-badge';
    badge.textContent = 'BRIEF';
    wrapper.prepend(badge);
  }

  // Education: lesson number badge + ruled-line texture
  if (vertical === 'education') {
    const badge = document.createElement('div');
    badge.className = 'pms-lesson-badge';
    badge.textContent = 'LESSON #' + Math.floor(Math.random() * 900 + 100);
    wrapper.prepend(badge);
    const ruled = document.createElement('div');
    ruled.className = 'pms-ruled-lines';
    wrapper.prepend(ruled);
  }

  // Nonprofit: impact label with heart + ripple texture
  if (vertical === 'nonprofit') {
    const badge = document.createElement('div');
    badge.className = 'pms-impact-badge';
    badge.innerHTML =
      `<span class="pms-impact-heart">\u2665</span>` +
      `<span class="pms-impact-label">IMPACT</span>`;
    wrapper.prepend(badge);
    const ripple = document.createElement('div');
    ripple.className = 'pms-ripple-texture';
    wrapper.prepend(ripple);
  }

  // Legal: case number badge + double-rule texture
  if (vertical === 'legal') {
    const badge = document.createElement('div');
    badge.className = 'pms-case-badge';
    badge.textContent = 'CASE #' + Math.floor(Math.random() * 9000 + 1000);
    wrapper.prepend(badge);
    const rule = document.createElement('div');
    rule.className = 'pms-double-rule';
    wrapper.prepend(rule);
  }

  // Fun: hacker terminal — "VITALS" status + blinking dot
  if (vertical === 'fun') {
    const status = document.createElement('div');
    status.className = 'pms-monitor-status';
    status.textContent = 'SYSTEM';
    wrapper.prepend(status);
    const dot = document.createElement('div');
    dot.className = 'pms-monitor-dot';
    wrapper.prepend(dot);
  }

  if (interactiveData?.prompt) {
    const prompt = document.createElement('div');
    prompt.className = 'pms-interactive-prompt';

    // SaaS / Tech: code comment prefix
    if (vertical === 'saas' || vertical === 'tech') {
      const prefix = document.createElement('span');
      prefix.className = 'pms-prompt-prefix';
      prefix.textContent = '# ';
      prompt.appendChild(prefix);
    }

    // Education: pencil icon prefix (teacher writing)
    if (vertical === 'education') {
      const prefix = document.createElement('span');
      prefix.className = 'pms-prompt-prefix';
      prefix.textContent = '\u270E ';
      prompt.appendChild(prefix);
    }

    // Nonprofit: heart prefix (community warmth)
    if (vertical === 'nonprofit') {
      const prefix = document.createElement('span');
      prefix.className = 'pms-prompt-prefix';
      prefix.textContent = '\u2661 ';
      prompt.appendChild(prefix);
    }

    // Legal: em dash prefix (formal citation style)
    if (vertical === 'legal') {
      const prefix = document.createElement('span');
      prefix.className = 'pms-prompt-prefix';
      prefix.textContent = '\u2014 ';
      prompt.appendChild(prefix);
    }

    // Restaurant: menu bullet prefix
    if (vertical === 'restaurant') {
      const prefix = document.createElement('span');
      prefix.className = 'pms-prompt-prefix';
      prefix.textContent = '\u25E6 ';
      prompt.appendChild(prefix);
    }

    // Ecommerce: browse chevron prefix
    if (vertical === 'ecommerce') {
      const prefix = document.createElement('span');
      prefix.className = 'pms-prompt-prefix';
      prefix.textContent = '\u203A ';
      prompt.appendChild(prefix);
    }

    // Healthcare: medical plus prefix
    if (vertical === 'healthcare') {
      const prefix = document.createElement('span');
      prefix.className = 'pms-prompt-prefix';
      prefix.textContent = '+ ';
      prompt.appendChild(prefix);
    }

    // Finance / Fintech: ticker arrow prefix
    if (vertical === 'finance' || vertical === 'fintech') {
      const prefix = document.createElement('span');
      prefix.className = 'pms-prompt-prefix';
      prefix.textContent = '\u25B8 ';
      prompt.appendChild(prefix);
    }

    // Real estate: compass diamond prefix
    if (vertical === 'real_estate') {
      const prefix = document.createElement('span');
      prefix.className = 'pms-prompt-prefix';
      prefix.textContent = '\u25C8 ';
      prompt.appendChild(prefix);
    }

    // Automotive: speed arrow prefix
    if (vertical === 'automotive') {
      const prefix = document.createElement('span');
      prefix.className = 'pms-prompt-prefix';
      prefix.textContent = '\u25B6 ';
      prompt.appendChild(prefix);
    }

    // Portfolio: creative tilde prefix
    if (vertical === 'portfolio') {
      const prefix = document.createElement('span');
      prefix.className = 'pms-prompt-prefix';
      prefix.textContent = '~ ';
      prompt.appendChild(prefix);
    }

    // Professional services: formal bullet prefix
    if (vertical === 'professional_services') {
      const prefix = document.createElement('span');
      prefix.className = 'pms-prompt-prefix';
      prefix.textContent = '\u2022 ';
      prompt.appendChild(prefix);
    }

    // Dental: sparkle/clean prefix
    if (vertical === 'dental') {
      const prefix = document.createElement('span');
      prefix.className = 'pms-prompt-prefix';
      prefix.textContent = '\u2726 ';
      prompt.appendChild(prefix);
    }

    // Agency: broadcast prefix
    if (vertical === 'agency') {
      const prefix = document.createElement('span');
      prefix.className = 'pms-prompt-prefix';
      prefix.textContent = '\u00BB ';
      prompt.appendChild(prefix);
    }

    // Fun: terminal prompt prefix
    if (vertical === 'fun') {
      const prefix = document.createElement('span');
      prefix.className = 'pms-prompt-prefix';
      prefix.textContent = '> ';
      prompt.appendChild(prefix);
    }

    const promptText = document.createElement('span');
    promptText.textContent = interactiveData.prompt;
    prompt.appendChild(promptText);
    wrapper.appendChild(prompt);
  }

  // ACTIONS TYPE - ensure actions is a valid array
  if (interactiveData?.type === 'actions' && Array.isArray(interactiveData.actions) && interactiveData.actions.length > 0) {
    renderActions(interactiveData, wrapper, onAction, vertical);
  }

  // CALCULATOR TYPE
  if (interactiveData?.type === 'calculator' && interactiveData.calculator) {
    renderCalculator(interactiveData, wrapper);
  }

  // SELECTOR TYPE
  if (interactiveData?.type === 'selector' && interactiveData.selector) {
    renderSelector(interactiveData, wrapper, onAction, vertical);
  }

  container.appendChild(wrapper);
});

/**
 * Render action buttons
 */
function renderActions(
  data: InteractiveData,
  wrapper: HTMLElement,
  onAction?: (value: string) => void,
  vertical?: string
): void {
  const actionsWrapper = document.createElement('div');
  actionsWrapper.className = 'pms-actions';

  for (const action of data.actions!) {
    // Skip actions with no label - don't render empty buttons
    if (!action.label?.trim()) continue;

    const btn = document.createElement('button');
    btn.className = `pms-action-btn ${action.variant === 'primary' ? 'pms-action-primary' : ''}`;

    // Vertical-specific prefix elements
    if (vertical === 'saas' || vertical === 'tech') {
      const prefix = document.createElement('span');
      prefix.className = 'pms-action-prefix';
      prefix.textContent = '$ ';
      btn.appendChild(prefix);
    } else if (vertical === 'restaurant') {
      const bullet = document.createElement('span');
      bullet.className = 'pms-action-bullet';
      bullet.textContent = '\u2022';
      btn.appendChild(bullet);
    } else if (vertical === 'education') {
      // Multiple-choice letter labels: A. B. C. D.
      const idx = Array.from(actionsWrapper.children).length;
      const letter = String.fromCharCode(65 + idx); // A, B, C, D...
      const prefix = document.createElement('span');
      prefix.className = 'pms-action-letter';
      prefix.textContent = letter + '.';
      btn.appendChild(prefix);
    } else if (vertical === 'nonprofit') {
      // Forward arrow: call to action
      const arrow = document.createElement('span');
      arrow.className = 'pms-action-arrow';
      arrow.textContent = '\u2192';
      btn.appendChild(arrow);
    } else if (vertical === 'legal') {
      // Section symbol: legal document marker
      const section = document.createElement('span');
      section.className = 'pms-action-section';
      section.textContent = '\u00A7';
      btn.appendChild(section);
    } else if (vertical === 'ecommerce') {
      // Cart numbering: #1 #2 #3
      const idx = Array.from(actionsWrapper.children).length;
      const prefix = document.createElement('span');
      prefix.className = 'pms-action-cart-num';
      prefix.textContent = '#' + (idx + 1);
      btn.appendChild(prefix);
    } else if (vertical === 'healthcare') {
      // Gentle triangle: care indicator
      const prefix = document.createElement('span');
      prefix.className = 'pms-action-care';
      prefix.textContent = '\u25B9 ';
      btn.appendChild(prefix);
    } else if (vertical === 'finance' || vertical === 'fintech') {
      // Ledger numbering: 01. 02. 03.
      const idx = Array.from(actionsWrapper.children).length;
      const prefix = document.createElement('span');
      prefix.className = 'pms-action-ledger';
      prefix.textContent = String(idx + 1).padStart(2, '0') + '.';
      btn.appendChild(prefix);
    } else if (vertical === 'real_estate') {
      // Square pin marker
      const prefix = document.createElement('span');
      prefix.className = 'pms-action-pin';
      prefix.textContent = '\u25AA ';
      btn.appendChild(prefix);
    } else if (vertical === 'automotive') {
      // Em dash: gauge tick
      const prefix = document.createElement('span');
      prefix.className = 'pms-action-gauge';
      prefix.textContent = '\u2014 ';
      btn.appendChild(prefix);
    } else if (vertical === 'portfolio') {
      // Star prefix
      const prefix = document.createElement('span');
      prefix.className = 'pms-action-star';
      prefix.textContent = '\u2726 ';
      btn.appendChild(prefix);
    } else if (vertical === 'professional_services') {
      // Roman numeral style: i. ii. iii.
      const romans = ['i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii'];
      const idx = Array.from(actionsWrapper.children).length;
      const prefix = document.createElement('span');
      prefix.className = 'pms-action-numeral';
      prefix.textContent = romans[idx] + '.';
      btn.appendChild(prefix);
    } else if (vertical === 'dental') {
      // Checkmark: clean confirmation
      const prefix = document.createElement('span');
      prefix.className = 'pms-action-check';
      prefix.textContent = '\u2713 ';
      btn.appendChild(prefix);
    } else if (vertical === 'agency') {
      // Arrow: broadcast direction
      const prefix = document.createElement('span');
      prefix.className = 'pms-action-broadcast';
      prefix.textContent = '\u2192 ';
      btn.appendChild(prefix);
    } else if (vertical === 'fun') {
      // Checkbox hack style
      const prefix = document.createElement('span');
      prefix.className = 'pms-action-hack';
      prefix.textContent = '[x] ';
      btn.appendChild(prefix);
    }

    const labelSpan = document.createElement('span');
    labelSpan.textContent = action.label;
    btn.appendChild(labelSpan);

    btn.addEventListener('click', () => {
      // Disable button to prevent spam clicking
      disableButtonTemporarily(btn, 2000);

      // Resolve the effective action value — AI may use "value", "direct", "action", or "url"
      const actionValue = action.value || action.direct || action.action;

      if (action.url) {
        navigateToUrl(action.url);
      } else if (actionValue) {
        if (actionValue.startsWith('link:')) {
          navigateToUrl(actionValue.replace('link:', ''));
        } else if (actionValue.startsWith('mailto:') || actionValue.startsWith('tel:')) {
          navigateToUrl(actionValue);
        } else if (actionValue.startsWith('redirect:')) {
          navigateToUrl(actionValue.replace('redirect:', ''));
        } else if (onAction) {
          onAction(actionValue);
        }
      } else if (onAction && action.label) {
        // Fallback: use label as action value if value is missing
        onAction(action.label);
      }
    });

    actionsWrapper.appendChild(btn);
  }

  wrapper.appendChild(actionsWrapper);
}

/**
 * Render calculator with slider - enhanced with gradient value display
 */
function renderCalculator(data: InteractiveData, wrapper: HTMLElement): void {
  const calc = data.calculator!;
  const calcWrapper = document.createElement('div');
  calcWrapper.className = 'pms-calculator';

  // Defensive defaults for required numeric fields
  const min = calc.min ?? 0;
  const max = calc.max ?? 100;
  const step = calc.step ?? 1;
  const defaultVal = calc.default ?? min;

  // Label
  const labelEl = document.createElement('label');
  labelEl.className = 'pms-calculator-label';
  labelEl.textContent = calc.label ?? 'Value';
  calcWrapper.appendChild(labelEl);

  // Value display with gradient text
  const valueDisplay = document.createElement('div');
  valueDisplay.className = 'pms-calculator-value-display';
  if (calc.prefix) {
    const prefix = document.createElement('span');
    prefix.className = 'pms-calculator-prefix';
    prefix.textContent = calc.prefix;
    valueDisplay.appendChild(prefix);
  }
  const amountEl = document.createElement('span');
  amountEl.className = 'pms-calculator-amount';
  valueDisplay.appendChild(amountEl);
  if (calc.suffix) {
    const suffix = document.createElement('span');
    suffix.className = 'pms-calculator-suffix';
    suffix.textContent = calc.suffix;
    valueDisplay.appendChild(suffix);
  }
  calcWrapper.appendChild(valueDisplay);

  // Slider wrapper
  const sliderWrapper = document.createElement('div');
  sliderWrapper.className = 'pms-calculator-slider-wrapper';

  const slider = document.createElement('input');
  slider.type = 'range';
  slider.className = 'pms-calculator-slider';
  slider.min = String(min);
  slider.max = String(max);
  slider.step = String(step);
  slider.value = String(defaultVal);

  sliderWrapper.appendChild(slider);

  // Range labels
  const rangeLabels = document.createElement('div');
  rangeLabels.className = 'pms-calculator-range';
  rangeLabels.innerHTML = `<span>${escapeHtml(calc.prefix ?? '')}${min.toLocaleString()}</span><span>${escapeHtml(calc.prefix ?? '')}${max.toLocaleString()}</span>`;
  sliderWrapper.appendChild(rangeLabels);

  calcWrapper.appendChild(sliderWrapper);

  // Result display
  const resultDisplay = document.createElement('div');
  resultDisplay.className = 'pms-calculator-result';

  function updateCalculator() {
    const val = parseFloat(slider.value);
    amountEl.textContent = val.toLocaleString();

    // Calculate result if formula provided
    if (calc.formula) {
      const result = evaluateFormula(calc.formula, val);
      if (result !== null) {
        const resultLabel = calc.resultLabel ?? 'Result';
        resultDisplay.innerHTML = `<span class="pms-calculator-result-label">${escapeHtml(resultLabel)}</span><span class="pms-calculator-result-value">${escapeHtml(calc.prefix ?? '')}${result.toLocaleString()}${escapeHtml(calc.suffix ?? '')}</span>`;
      } else {
        resultDisplay.textContent = '';
      }
    }
  }

  slider.addEventListener('input', updateCalculator);
  updateCalculator();

  if (calc.formula) {
    calcWrapper.appendChild(resultDisplay);
  }

  wrapper.appendChild(calcWrapper);
}

/**
 * Render option selector - enhanced with checkmark icons
 * Vertical-aware: SaaS gets toggle switches, ecommerce gets dot indicators
 */
function renderSelector(
  data: InteractiveData,
  wrapper: HTMLElement,
  onAction?: (value: string) => void,
  vertical?: string
): void {
  const sel = data.selector!;
  const selectorWrapper = document.createElement('div');
  selectorWrapper.className = 'pms-selector';

  const selectedValues = new Set<string>();
  const optionsWrapper = document.createElement('div');
  optionsWrapper.className = 'pms-selector-options';

  for (const option of sel.options) {
    const optionEl = document.createElement('button');
    optionEl.className = 'pms-selector-option';
    const optValue = option.value ?? option.label;
    optionEl.dataset['value'] = optValue;

    // Vertical-specific indicator elements
    if (vertical === 'saas' || vertical === 'tech') {
      // SaaS / Tech: toggle switch (track + sliding dot)
      const toggle = document.createElement('span');
      toggle.className = 'pms-selector-toggle';
      const dot = document.createElement('span');
      dot.className = 'pms-selector-toggle-dot';
      toggle.appendChild(dot);
      optionEl.appendChild(toggle);
    } else if (vertical === 'ecommerce') {
      // Ecommerce: selection dot indicator
      const dotEl = document.createElement('span');
      dotEl.className = 'pms-selector-dot';
      optionEl.appendChild(dotEl);
    } else if (vertical === 'education') {
      // Education: bubble/scantron circle (like a test answer sheet)
      const bubble = document.createElement('span');
      bubble.className = 'pms-selector-bubble';
      optionEl.appendChild(bubble);
    } else if (vertical === 'nonprofit') {
      // Nonprofit: heart indicator
      const heart = document.createElement('span');
      heart.className = 'pms-selector-heart';
      heart.textContent = '\u2661';
      optionEl.appendChild(heart);
    } else if (vertical === 'legal') {
      // Legal: formal square checkbox
      const box = document.createElement('span');
      box.className = 'pms-selector-box';
      optionEl.appendChild(box);
    } else if (vertical === 'restaurant') {
      // Restaurant: filled menu dot
      const menuDot = document.createElement('span');
      menuDot.className = 'pms-selector-menu-dot';
      menuDot.textContent = '\u25CF';
      optionEl.appendChild(menuDot);
    } else if (vertical === 'healthcare') {
      // Healthcare: cross indicator
      const cross = document.createElement('span');
      cross.className = 'pms-selector-cross';
      cross.textContent = '+';
      optionEl.appendChild(cross);
    } else if (vertical === 'finance' || vertical === 'fintech') {
      // Finance / Fintech: formal square
      const square = document.createElement('span');
      square.className = 'pms-selector-square';
      optionEl.appendChild(square);
    } else if (vertical === 'real_estate') {
      // Real estate: pin indicator
      const pin = document.createElement('span');
      pin.className = 'pms-selector-pin';
      pin.textContent = '\u25C9';
      optionEl.appendChild(pin);
    } else if (vertical === 'automotive') {
      // Automotive: gauge dot
      const gauge = document.createElement('span');
      gauge.className = 'pms-selector-gauge';
      gauge.textContent = '\u25C9';
      optionEl.appendChild(gauge);
    } else if (vertical === 'portfolio') {
      // Portfolio: cursor dot
      const cursor = document.createElement('span');
      cursor.className = 'pms-selector-cursor';
      cursor.textContent = '\u25E6';
      optionEl.appendChild(cursor);
    } else if (vertical === 'professional_services') {
      // Professional services: formal box
      const formalBox = document.createElement('span');
      formalBox.className = 'pms-selector-formal';
      optionEl.appendChild(formalBox);
    } else if (vertical === 'dental') {
      // Dental: clean circle
      const clean = document.createElement('span');
      clean.className = 'pms-selector-clean';
      clean.textContent = '\u25CB';
      optionEl.appendChild(clean);
    } else if (vertical === 'agency') {
      // Agency: broadcast dot
      const broadcast = document.createElement('span');
      broadcast.className = 'pms-selector-broadcast';
      broadcast.textContent = '\u25C9';
      optionEl.appendChild(broadcast);
    } else if (vertical === 'fun') {
      // Fun: matrix square
      const matrix = document.createElement('span');
      matrix.className = 'pms-selector-matrix';
      matrix.textContent = '\u25AA';
      optionEl.appendChild(matrix);
    } else {
      // Default: checkmark box
      const checkEl = document.createElement('span');
      checkEl.className = 'pms-selector-option-check';
      optionEl.appendChild(checkEl);
    }

    // Add label text
    const labelEl = document.createElement('span');
    labelEl.textContent = option.label;
    optionEl.appendChild(labelEl);

    optionEl.setAttribute('role', sel.multi ? 'checkbox' : 'radio');
    if (option.selected) {
      optionEl.classList.add('pms-selector-option-selected');
      optionEl.setAttribute('aria-checked', 'true');
      selectedValues.add(optValue);
    } else {
      optionEl.setAttribute('aria-checked', 'false');
    }

    optionEl.addEventListener('click', () => {
      if (sel.multi) {
        if (selectedValues.has(optValue)) {
          selectedValues.delete(optValue);
          optionEl.classList.remove('pms-selector-option-selected');
          optionEl.setAttribute('aria-checked', 'false');
        } else {
          selectedValues.add(optValue);
          optionEl.classList.add('pms-selector-option-selected');
          optionEl.setAttribute('aria-checked', 'true');
        }
      } else {
        // Single select: deselect all others
        selectedValues.clear();
        optionsWrapper.querySelectorAll('.pms-selector-option').forEach(el => {
          el.classList.remove('pms-selector-option-selected');
          el.setAttribute('aria-checked', 'false');
        });
        selectedValues.add(optValue);
        optionEl.classList.add('pms-selector-option-selected');
        optionEl.setAttribute('aria-checked', 'true');
      }
    });

    optionsWrapper.appendChild(optionEl);
  }

  selectorWrapper.appendChild(optionsWrapper);

  if (sel.submitLabel) {
    const submitBtn = document.createElement('button');
    submitBtn.className = 'pms-selector-submit';
    submitBtn.textContent = sel.submitLabel;
    submitBtn.addEventListener('click', () => {
      if (onAction && selectedValues.size > 0) {
        onAction(Array.from(selectedValues).join(','));
      }
    });
    selectorWrapper.appendChild(submitBtn);
  }

  wrapper.appendChild(selectorWrapper);
}
